import { QESButton } from "./qes-standards";
import { FC, JSX, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { AccordionCollapseContext } from "./AccordionsCollapseContext";
import { expandCollapse } from "./qes-standards/utility/expandCollapse";

export interface AccordionProps {
  children?: React.ReactNode;
  /**
   * An optional classname to add to the outermost div in the component
   */
  className?: string;
  /**
   * Determines whether the accordion is initially rendered opened or closed
   */
  defaultOpen?: boolean;
  /**
   * If true, the component won't load its children until the first time it's opened. Useful if the children require a lot of processing or API calls for something that might not be used/seen.
   */
  lazyLoad?: boolean;
  /**
   * So QA can do automation testing!
   */
  testId: string;
  /**
   * The title displayed on the always-visible part of the accordion
   */
  title: string | JSX.Element;
  /**
   * An optional disable
   */
  disabled?: boolean;
  /**
   * A function to run before the state update to open the accordion
   */
  onPanelWillOpen?: () => void;
  /**
   * A function to run after the state update to open the accordion
   */
  onPanelDidOpen?: () => void;
  /**
   * A function to run before the state update to close the accordion
   */
  onPanelWillClose?: () => void;
  /**
   * A function to run after the state update to close the accordion
   */
  onPanelDidClose?: () => void;
}

/**
 * Accordion component, used to dynamically show or hide content. The "hidden" content is always 
 * visible in the DOM, unless the lazyLoad prop is true and the accordion has not been opened yet.
 *  <br>
 *  The functionality of closing other accordions when one is opened relies on having an ancestor AccordionContainer.
 */
export const Accordion: FC<AccordionProps> = (props: AccordionProps) => {
  // if there's no ancestor AccordionCollapseContext, this returns an empty object ({})
  const accordionContext = useContext(AccordionCollapseContext);
  // we want this component to function with or without an ancestor AccordionContainer
  const [isOpenState, setIsOpenState] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const chevronRef = useRef<HTMLElement>(null);
  const collapsingRef = useRef<HTMLDivElement>(null);
  const accordionId = useId();
  const hasOpened = useRef(false);

  useEffect(() => {
    const contextOpen = accordionContext?.expandedAccordion === panelId;
    if (accordionContext.expandedAccordion !== undefined && contextOpen !== isOpenState) {
      setIsOpenState(contextOpen);
    }
  }, [accordionContext?.expandedAccordion]);

  const isOpen = useMemo(() => {
    return accordionContext.expandedAccordion === panelId || isOpenState;
  }, [accordionContext?.expandedAccordion, isOpenState]);

  useEffect(() => {
    if (isOpen) {
      if (props.onPanelDidOpen) {
        props.onPanelDidOpen();
      }
    } else if (hasOpened.current) {
      if (props.onPanelDidClose) {
        props.onPanelDidClose();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (hasOpened.current) {
      expandCollapse(collapsingRef, props.onPanelWillClose, props.onPanelWillOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    if (props.defaultOpen) {
      onOpenAccordion();
    }
  }, [props.defaultOpen]);

  const onCloseAccordion = () => {
    if (accordionContext.setExpandedAccordion) {
      accordionContext?.setExpandedAccordion("");
    }
    setIsOpenState(false);
  }

  const onOpenAccordion = () => {
    hasOpened.current = true;
    if (accordionContext.setExpandedAccordion) {
      accordionContext?.setExpandedAccordion(panelId);
    }
    setIsOpenState(true);
  }

  const onClick = () => {
    if (isOpen) {
      onCloseAccordion();
    } else {
      onOpenAccordion();
    }
  }

  return (
    <div id={accordionId} className={`qes-accordion ${props.className || ""}`} ref={accordionRef}>
      <div className="qes-accordion__header" id={`${panelId}-header`}>
        <QESButton
          qesType="secondary"
          className="qes-accordion__button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          qesTestId={props.testId}
          disabled={props.disabled ?? false}
          aria-pressed={isOpen}
          onClick={onClick}
        >
          <span>{props.title}</span>
          <span className="collapse-indicator" style={{}} ref={chevronRef}><i className="fa fa-fw fa-chevron-down" /></span>
        </QESButton>
      </div>

      <div
        ref={collapsingRef}
        className={`qes-accordion__body-wrapper qes-js-closed`}
        aria-labelledby={`${panelId}-header`}
      >
        <div className="qes-accordion__body">
          <div className="row">
            <div className="col-12">
              {!props.lazyLoad || hasOpened ? props.children : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

